import { defaultBookAttributes, type SpreadLayoutId } from '@kitup/book-schema';
import type { BookPreviewDto, BookPreviewSceneDto } from '../api/book-preview.js';
import type { Dictionary } from '../i18n/registry.js';

/** Идентификатор демо-книги; по нему же читалка хранит настройки чтения. */
export const DEMO_BOOK_ID = 'demo';

/** Ссылки демо-книги не подписываются, но тип превью требует срок годности. */
const NEVER_EXPIRES = '2100-01-01T00:00:00.000Z';

function svg(width: number, height: number, body: string): string {
  return (
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${body}</svg>`,
    )
  );
}

function sky(id: string, from: string, to: string): string {
  return `<defs><linearGradient id="${id}" x2="1" y2="1"><stop stop-color="${from}"/><stop offset="1" stop-color="${to}"/></linearGradient></defs>`;
}

/**
 * Иллюстрации демо-книги — рисованные заглушки в самом файле.
 *
 * Никаких сетевых запросов и никаких настоящих детских фотографий:
 * демо должно открываться у гостя, ещё не заведшего аккаунт.
 */
const ILLUSTRATIONS: readonly string[] = [
  svg(
    900,
    1280,
    `${sky('a', '#f6d9b8', '#c9a884')}<rect width="900" height="1280" fill="url(#a)"/>
     <rect x="120" y="360" width="660" height="470" rx="28" fill="#fdf6e8" transform="rotate(-4 450 595)"/>
     <path d="M200 620 q120-90 240 0 t240-40" stroke="#a9683f" stroke-width="10" fill="none"/>
     <circle cx="640" cy="520" r="26" fill="#e8734a"/>
     <path d="M0 980 q220-120 450 0 t450-40 V1280 H0Z" fill="#8a6a58"/>`,
  ),
  svg(
    1600,
    1120,
    `${sky('b', '#a8d7db', '#f0c5ad')}<rect width="1600" height="1120" fill="url(#b)"/>
     <circle cx="1300" cy="220" r="96" fill="#fff1a6" opacity=".9"/>
     <path d="M0 820 Q300 660 620 820 T1180 790 T1600 830 V1120 H0Z" fill="#789e70"/>
     <ellipse cx="700" cy="760" rx="70" ry="96" fill="#f7e2cb"/>
     <path d="M660 690 q40-120 80 0" fill="#cbb6a2"/>
     <rect x="980" y="640" width="220" height="180" rx="18" fill="#fdf6e8"/>`,
  ),
  svg(
    1600,
    800,
    `${sky('c', '#5f6fa8', '#b58fbf')}<rect width="1600" height="800" fill="url(#c)"/>
     <path d="M1180 120 l26 74 78 6-60 50 20 76-64-44-64 44 20-76-60-50 78-6Z" fill="#fff3b0"/>
     <path d="M0 560 Q260 420 520 560 T1080 540 T1600 580 V800 H0Z" fill="#3f5a52"/>
     <circle cx="560" cy="520" r="42" fill="#f7e2cb"/>
     <circle cx="640" cy="536" r="30" fill="#e7d3bd"/>`,
  ),
  svg(
    1600,
    1120,
    `${sky('d', '#2f3557', '#6b5a83')}<rect width="1600" height="1120" fill="url(#d)"/>
     <path d="M540 700 h520 v420 H540Z" fill="#4a3d52"/>
     <rect x="700" y="790" width="200" height="160" rx="12" fill="#ffd98a"/>
     <path d="M500 700 l300-210 300 210Z" fill="#5e4b63"/>
     <circle cx="1320" cy="200" r="60" fill="#fff1a6" opacity=".85"/>`,
  ),
];

/** Раскладки идут вперемешку: демо заодно показывает все три вида разворотов. */
const LAYOUTS: readonly SpreadLayoutId[] = ['split', 'full-bleed', 'band', 'split'];

const COVER = svg(
  1200,
  1600,
  `${sky('e', '#a8d7db', '#f0c5ad')}<rect width="1200" height="1600" fill="url(#e)"/>
   <path d="M860 300 l30 86 90 8-70 58 24 88-74-52-74 52 24-88-70-58 90-8Z" fill="#fff3b0"/>
   <path d="M0 1080 Q300 940 600 1080 T1200 1050 V1600 H0Z" fill="#789e70"/>
   <ellipse cx="520" cy="1000" rx="86" ry="118" fill="#f7e2cb"/>`,
);

/**
 * Демонстрационная книга для знакомства с продуктом (E15-T2).
 *
 * Собирается из словаря локали: тексты разворотов — обычные пользовательские
 * строки и живут там же, где остальные. Данные полностью статичны, поэтому
 * страница `/demo` работает и без аккаунта, и без единой генерации.
 */
/** `locale`: the demo's words are the interface's, so its text is in that language. */
export function demoBook(t: Dictionary, locale: string): BookPreviewDto {
  const attributes = defaultBookAttributes();
  const scenes: readonly BookPreviewSceneDto[] = t.demo.spreads.map((textRu, index) => ({
    id: `${DEMO_BOOK_ID}-scene-${index}`,
    index,
    layoutId: LAYOUTS[index % LAYOUTS.length] ?? null,
    textRu,
    textBlocks: [],
    bandImageRatio: null,
    image: { url: ILLUSTRATIONS[index % ILLUSTRATIONS.length] ?? '', urlExpiresAt: NEVER_EXPIRES },
    video: null,
    narration: null,
  }));

  return {
    bookId: DEMO_BOOK_ID,
    title: t.demo.title,
    language: locale,
    subtitle: t.demo.subtitle,
    dedication: t.demo.dedication,
    attributes,
    cover: { url: COVER, urlExpiresAt: NEVER_EXPIRES },
    backCover: { url: COVER, urlExpiresAt: NEVER_EXPIRES },
    scenes,
  };
}
